import { Component, Input, OnChanges } from '@angular/core';
import { catchError, EMPTY, finalize, of } from 'rxjs';
import { ImageCacheService } from 'src/app/project/directives/secure-src/image-cache.service';
import { ReactWrapperDirective } from 'src/app/project/directives/react-wrapper-directive';
import DigitalSignature, {
  DigitalSignatureProps,
} from 'react-lib/components/organisms/DigitalSignature';
import { TDigitalSignatureValues } from 'react-lib/components/molecules/AddDigitalSignatureModal';
import { TUser } from '../../user.model';
import { UserSettingsService } from '../user-settings.service';
import { logErrorInSentry } from '../../../errors/response-error';

@Component({
  standalone: true,
  selector: 'pp-user-settings-digital-signature',
  templateUrl: './user-settings-digital-signature.html',
  styleUrls: ['./user-settings-digital-signature.scss'],
  imports: [ReactWrapperDirective],
})
export class UserSettingsDigitalSignatureComponent implements OnChanges {
  @Input() ppUser: TUser;

  DigitalSignature = DigitalSignature;

  private savedSignature: TDigitalSignatureValues | null = null;

  private storedSignatureImage: Blob | null = null;

  private isRemovingSignature = false;

  props = this.toProps();

  constructor(
    private userSettingsService: UserSettingsService,
    private imageCacheService: ImageCacheService,
  ) {}

  ngOnChanges(): void {
    this.loadStoredSignatureImage();
    this.props = this.toProps();
  }

  private loadStoredSignatureImage(): void {
    const svgUrl = this.ppUser?.signature?.svgUrl;

    if (!svgUrl) {
      this.storedSignatureImage = null;
      return;
    }

    this.imageCacheService
      .getImageUrl(svgUrl)
      .pipe(catchError(() => EMPTY))
      .subscribe((image) => {
        this.storedSignatureImage = image;
        this.props = this.toProps();
      });
  }

  private toProps(): DigitalSignatureProps {
    return {
      onSave: this.saveSignature.bind(this),
      onRemove: this.removeSignature.bind(this),
      isRemoving: this.isRemovingSignature,
      signature: this.toDisplayedSignature(),
    };
  }

  private toDisplayedSignature(): DigitalSignatureProps['signature'] {
    if (this.savedSignature) {
      return {
        user: this.savedSignature.name,
        role: this.savedSignature.role,
        svg: this.savedSignature.svg,
      };
    }

    const storedSignature = this.ppUser?.signature;

    if (storedSignature) {
      return {
        user: storedSignature.name,
        role: storedSignature.role,
        svg: this.storedSignatureImage,
      };
    }

    return { user: this.ppUser?.userName ?? '', role: '', svg: null };
  }

  private saveSignature(formValues: TDigitalSignatureValues): void {
    if (!this.ppUser?.userId) {
      return;
    }

    this.userSettingsService.saveSignature(this.ppUser.userId, formValues).subscribe(() => {
      this.savedSignature = formValues;
      this.props = this.toProps();
    });
  }

  private removeSignature(): void {
    if (!this.ppUser?.userId) {
      return;
    }

    this.isRemovingSignature = true;
    this.props = this.toProps();

    this.userSettingsService
      .removeSignature(this.ppUser.userId)
      .pipe(
        catchError((error) => {
          logErrorInSentry({
            error,
            message: 'Error removing signature from user profile settings',
          });

          return of();
        }),
        finalize(() => {
          this.isRemovingSignature = false;
          this.props = this.toProps();
        }),
      )
      .subscribe(() => {
        this.savedSignature = null;
        this.storedSignatureImage = null;
        this.props = this.toProps();
      });
  }
}
